import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// An account an admin creates starts with a temporary password that was sent by
// email. Until the customer replaces it, this covers the whole site: the only
// ways out are choosing a new password or signing out. The server clears the
// flag on change-password (and when a reset link is completed), and refreshUser
// picks that up, so the gate closes itself.
const ForcePasswordChangeGate = () => {
  const { user, isAuthenticated, logout, refreshUser } = useAuth();
  const { pathname } = useLocation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // The admin panel signs in separately, and a reset link must always stay
  // usable: it is the way back for a customer who lost the invite email.
  const exempt = pathname.startsWith('/admin') || pathname.startsWith('/reset-password');
  if (!isAuthenticated || !user || !user.must_change_password || exempt) return null;

  const onType = (setter) => (e) => {
    setter(e.target.value);
    if (error) setError(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      setError('Please choose a password of at least 8 characters.');
      return;
    }
    if (next !== confirm) {
      setError('Those passwords do not match.');
      return;
    }
    if (next === current) {
      setError('Your new password must be different from the temporary one.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post('/auth/change-password', {
        current_password: current,
        new_password: next,
      });
    } catch (err) {
      setError(
        err.response?.data?.detail || 'Could not update your password. Please try again.'
      );
      setSaving(false);
      return;
    }
    try {
      await refreshUser();
    } catch {
      // The password did change; reload so the account is read afresh.
      window.location.reload();
    }
  };

  const signOut = () => {
    logout();
    window.location.href = '/';
  };

  const inputClass =
    'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-orange-400 focus:outline-none';

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="force-password-title"
    >
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
        <h2 id="force-password-title" className="text-xl font-bold text-gray-900">
          Choose your own password
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          You signed in with the temporary password we emailed you. Set a password of
          your own to continue. The temporary one stops working as soon as you do.
        </p>

        <form onSubmit={submit} className="mt-5 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="fpc-current">
              Temporary password
            </label>
            <input
              id="fpc-current"
              type="password"
              autoComplete="current-password"
              autoFocus
              value={current}
              onChange={onType(setCurrent)}
              required
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="fpc-next">
              New password
            </label>
            <input
              id="fpc-next"
              type="password"
              autoComplete="new-password"
              placeholder="At least 8 characters"
              value={next}
              onChange={onType(setNext)}
              required
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="fpc-confirm">
              Confirm new password
            </label>
            <input
              id="fpc-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={onType(setConfirm)}
              required
              className={inputClass}
            />
          </div>

          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={saving}
            className="w-full rounded-lg bg-orange-500 px-4 py-2.5 font-medium text-white hover:bg-orange-600 disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Save and continue'}
          </button>
        </form>

        <button
          type="button"
          onClick={signOut}
          className="mt-3 w-full text-center text-sm text-gray-500 hover:text-gray-700"
        >
          Sign out
        </button>
      </div>
    </div>
  );
};

export default ForcePasswordChangeGate;
